// AI Generated Test
import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { PolicyHelperService } from '@client/app/blocks/helpers/policy-helper/policy.helper.service';
import { activeFeatures } from '@monsido/core/constants/general.constant';

@Component({
    selector: 'page-details-policies-table',
    standalone: false,
    template: `
        <div class="max-height d-flex flex-column">
            <page-details-sections-tabs [tab]="tab" [options]="tabOptions"
                (onTabChange)="customTabChange($event.tab)" class="block pl-4 border-bottom">
                <li role="menuitem" [attr.aria-label]="'Filter' | translate">
                    <filter-button
                            [showFiltersBtn]="showFilters"
                            [(isFiltersOpen)]="isFiltersOpen">
                    </filter-button>
                </li>
                <li [ngClass]="{'active': tab === 'ignored'}"
                    data-test="ignored-policies-btn"
                    role="menuitem"
                    [attr.aria-label]="'Ignored Policies' | translate"
                    monEventTrigger="actionTrigger"
                    [monEventParams]="{action: 'ignored-policies-btn'}"
                    (click)="customTabChange('ignored')"
                    class="pr-2 align-self-center text-link text-nowrap cursor-pointer">
                    <span class="fas fa-eye-slash"></span>
                    <span translate>Ignored policies</span>
                </li>
                <li [ngClass]="{'active': tab === 'passed'}" role="menuitem"
                    data-test="passed-policies-btn"
                    [attr.aria-label]="'Passed Policy checks' | translate"
                    (click)="customTabChange('passed')"
                    monEventTrigger="actionTrigger"
                    [monEventParams]="{action:'passed-policy-checks-btn'}"
                    class="px-2 align-self-center text-link text-nowrap cursor-pointer">
                    <span class="fas fa-check"></span>
                    <span translate>Passed policy checks</span>
                </li>
            </page-details-sections-tabs>
            <div class="flex-grow-1">
                <mon-spinner [progress]="progress">
                    <div class="pt-5">
                        <table-filters [(open)]="isFiltersOpen"
                                       [(ngModel)]="filters"
                                       name="filters"
                                       [filters]="availableFilters"
                                       (onChanges)="onFiltersChange($event)">
                        </table-filters>
                    </div>
                    <table class="table table-hover table-fixed">
                        <thead>
                        <tr>
                            <th class="col-sm-8 col-md-6 col-lg-4"></th>
                            <th class="col-sm-14 col-md-14 col-lg-12" translate>Name</th>
                            <th class="col-sm-11 col-md-15 col-lg-21" translate>Note</th>
                            <th class="col-sm-15 col-md-13 col-lg-11 text-center" translate>Priority</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr [ngClass]="{'selected': currentPolicy === policy }"
                            (click)="onSelectPolicy(currentPolicy)"
                            *ngFor="let currentPolicy of filterPolicies(); trackBy: trackByPolicyId">
                            <td class="text-v-middle text-center" [attr.aria-label]="'Icon status'| translate">
                                <mon-table-policy-type
                                    *ngIf="currentPolicy.state !== 'running'"
                                    class="inline-block inner-t-mini inner-b-mini"
                                    [color]="currentPolicy.ignored ? 'grey' : 'link'"
                                    [passed]="currentPolicy.matches_count === 0 && !currentPolicy.ignored"
                                    [target]="getPolicyTarget(currentPolicy)"
                                    [tooltip]="getTooltipMessage(currentPolicy)"
                                >
                                </mon-table-policy-type>
                                <span *ngIf="currentPolicy.state === 'running'"
                                      [attr.aria-label]="'Running'| translate">
                                      <span class="fas fa-2x fa-spinner fa-spin inner-t-mini inner-b-mini"></span>
                                </span>
                            </td>
                            <td class="text-v-middle">
                                <span [attr.data-test]="'policy-' + currentPolicy.name | e2eAttributeEncoding"
                                      [ngClass]="{'text-grey': currentPolicy.ignored }">{{ currentPolicy.name }}</span>
                            </td>
                            <td class="text-v-middle">
                                <div class="text-ellipsis" [ngClass]="{'text-grey': currentPolicy.ignored }">
                                    <span class="fas fa-comment" *ngIf="currentPolicy.note?.length > 0"></span>
                                    <span>{{ currentPolicy.note }}</span>
                                </div>
                            </td>
                            <td class="text-v-middle text-center">
                        <span *ngIf="!currentPolicy.ignored"
                              class="label px-2 inner-t-smurf inner-b-smurf" [ngClass]="{
                              'label-info': currentPolicy.priority === 'low',
                              'label-warning': currentPolicy.priority === 'medium',
                              'label-danger': currentPolicy.priority === 'high'
                              }">{{ currentPolicy.priority | capitalize }}
                        </span>
                                <span *ngIf="currentPolicy.ignored" class="text-grey" translate>
                            Ignored
                        </span>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </mon-spinner>
            </div>
        </div>
    `,
})
export class PageDetailsPoliciesTableComponent implements OnInit {
    @Input() policies: any[] = [];
    @Input() policy: any;
    @Input() progress: any;
    @Output() selectPolicy = new EventEmitter<any>();
    @Output() getPolicies = new EventEmitter<any>();
    @Output() onTabChange = new EventEmitter<any>();
    @Output() onFilterChange = new EventEmitter<any>();

    showFilters: boolean = false;
    isFiltersOpen: boolean = false;
    availableFilters: any = {};
    filters: any = {};
    tabOptions: any[] = [];
    tab: any = null;

    constructor (
        private translateService: TranslateService,
        private policyHelperService: PolicyHelperService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    private activate (): void {
        this.showFilters = activeFeatures.table_filters;
        this.isFiltersOpen = false;
        this.setupFilters();
        this.setupTabs();
    }

    private setupFilters (): void {
        this.availableFilters = {};
        this.availableFilters.scheduled = [
            {
                name: this.translateService.getString('Scheduled'),
                value: true,
            },
            {
                name: this.translateService.getString('Not scheduled'),
                value: false,
            },
        ];
        this.filters = {};
        this.filters.scheduled = [];
    }

    private setupTabs (): void {
        this.tabOptions = [
            {
                name: this.translateService.getString('All'),
                value: null,
            },
            {
                name: this.translateService.getString('Unwanted'),
                value: 'unwanted',
            },
            {
                name: this.translateService.getString('Required'),
                value: 'required',
            },
            {
                name: this.translateService.getString('Matches'),
                value: 'search',
            },
        ];

        this.tab = this.tabOptions[0].value;
    }

    customTabChange (tab: any): void {
        this.tab = tab;
        this.onTabChange.emit({ tab: tab });
    }

    filterPolicies (): any[] {
        if (!this.policies) {
            return [];
        }
        const sortedPolicies = this.moveNotPassedPoliciesToTheTop(this.policies);
        if (this.tab === 'ignored') {
            return this.onlyIgnored(sortedPolicies);
        } else if (this.tab === 'passed') {
            return this.onlyPassedChecks(sortedPolicies);
        } else {
            return sortedPolicies;
        }
    }

    private onlyIgnored (policies: any[]): any[] {
        return policies.filter((policy: any) => {
            return policy.ignored;
        });
    }

    private onlyPassedChecks (policies: any[]): any[] {
        return policies.filter((policy: any) => {
            return !policy.ignored && policy.matches_count === 0 && policy.state !== 'running';
        });
    }

    getPolicyTarget (policy: any): any {
        return this.policyHelperService.getType(policy);
    }

    getTooltipMessage (policy: any): any {
        return this.policyHelperService.getTooltipMessageForType(policy);
    }

    onFiltersChange (changes: any): void {
        this.onFilterChange.emit({ changes: changes });
        if (changes.scheduled && changes.scheduled.length > 0) {
            const schedule = changes.scheduled[changes.scheduled.length - 1];
            this.filters.scheduled = [schedule];
        }
    }

    onSelectPolicy (policy: any): void {
        this.selectPolicy.emit({ policy: policy });
    }

    private moveNotPassedPoliciesToTheTop (policies: any[]): any[] {
        const notPassedPolicies = policies.filter((policy: any) => {
            if (policy.matches_count > 0) {
                return policy;
            }
        });
        const passedPolicies = policies.filter((policy: any) => {
            if (policy.matches_count === 0) {
                return policy;
            }
        });
        return notPassedPolicies.concat(passedPolicies);
    }

    trackByPolicyId (index: number, policy: any): any {
        return policy.id;
    }
}
